import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Stars } from "../components/Stars";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, MapPin, Briefcase, Calendar } from "lucide-react";

export default function ProProfile() {
  const { userId } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sectors: [] });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/profiles/${userId}`);
      setProfile(r.data);
    } catch {
      toast.error("Profil introuvable");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const sectorLabel = (id) => {
    const s = meta.sectors.find((x) => x.id === id);
    return s ? (lang === "fr" ? s.fr : s.en) : id;
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    if (!rating) { toast.error(lang === "fr" ? "Choisissez une note" : "Pick a rating"); return; }
    setBusy(true);
    try {
      await api.post(`/profiles/${userId}/reviews`, { rating, comment });
      toast.success(lang === "fr" ? "Avis publié !" : "Review posted!");
      setRating(0); setComment("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !profile) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-muted-foreground">…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button onClick={() => navigate("/pros")} data-testid="back-to-pros" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> {t("back")}
      </button>

      <div className="rounded-3xl border border-border bg-card p-8 shadow-sm mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <Avatar className="h-24 w-24">
            <AvatarImage src={mediaUrl(profile.photo_url)} alt={profile.display_name} />
            <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">{profile.display_name?.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="pro-name">{profile.display_name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Stars value={profile.avg_rating} size={18} />
              <span className="text-sm text-muted-foreground">{profile.avg_rating > 0 ? profile.avg_rating : "—"} · {profile.review_count} {t("reviews")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-muted-foreground">
              {profile.city && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{profile.city}</span>}
              {profile.years_experience ? <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{profile.years_experience} {t("years_exp")}</span> : null}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {profile.trade.map((tr) => (
                <Badge key={tr} variant="secondary" className="rounded-full gap-1"><Briefcase className="h-3 w-3" />{sectorLabel(tr)}</Badge>
              ))}
            </div>
          </div>
        </div>
        {profile.bio && <p className="text-muted-foreground leading-relaxed mt-6 border-t border-border pt-6">{profile.bio}</p>}
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <h2 className="font-display text-xl font-semibold mb-4">{t("reviews")} ({profile.review_count})</h2>
          {profile.reviews.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="no-reviews">{t("no_reviews")}</p>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {profile.reviews.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{r.author_name}</span>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sticky top-24">
            <h3 className="font-display text-lg font-semibold mb-4">{t("leave_review")}</h3>
            {user && user.user_id === profile.user_id ? (
              <p className="text-sm text-muted-foreground">{lang === "fr" ? "C'est votre profil." : "This is your profile."}</p>
            ) : (
              <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
                <div>
                  <p className="text-sm font-medium mb-2">{t("your_rating")}</p>
                  <Stars value={rating} size={28} onChange={setRating} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">{t("comment")}</p>
                  <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" placeholder="…" />
                </div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="submit-review-btn">
                  {busy ? t("processing") : t("submit_review")}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
